import PageHero from "@/components/PageHero";
import SectionLabel from "@/components/SectionLabel";
import EditorialBanner from "@/components/EditorialBanner";
import {
  Database,
  Lock,
  Network,
  Cpu,
  Gauge,
  Scale,
  Shield,
  FileSearch,
  ArrowRight,
  CheckCircle2,
} from "lucide-react";

const PIPELINE = [
  { idx: "01", title: "Ingest", icon: Database, body: "Vendor-agnostic ingestion of what your stack already produces." },
  { idx: "02", title: "Sanitise", icon: Lock, body: "PII protected end to end. GDPR by design." },
  { idx: "03", title: "Entity Resolution", icon: Network, body: "Fragments merged into canonical entities, with confidence." },
  { idx: "04", title: "Risk Features", icon: Cpu, body: "Composed deterministically across risk components." },
  { idx: "05", title: "Trust Score", icon: Gauge, body: "An independent, comparable risk score — produced the same way, every time." },
  { idx: "06", title: "Gap Analysis", icon: Scale, body: "Where Sentinel agrees with your rating, and where it diverges." },
  { idx: "07", title: "FPRE & FFPRE", icon: Shield, body: "Twin false-positive reduction engines for AML and fraud." },
  { idx: "08", title: "Explain", icon: FileSearch, body: "Top contributors, cited to regulation." },
];

export default function Product() {
  return (
    <div data-testid="product-page">
      <PageHero
        eyebrow="Product"
        title="Two outcomes. Engineered to be defensible."
        sub="Sentinel is built around two problems — false-positive reduction across AML and fraud, and the missed risk that hides when entity resolution and cross-system context are absent. Everything else in the product exists to make those outcomes defensible to a supervisor."
        breadcrumb={[{ label: "Lexifide.ai", to: "/" }, { label: "Product" }]}
        testId="product-hero"
      />

      {/* Pipeline */}
      <section id="pipeline" className="light-section py-24 lg:py-32">
        <div className="max-w-[1380px] mx-auto px-6 lg:px-10">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 mb-14">
            <div className="lg:col-span-7">
              <SectionLabel index="01" label="Deterministic pipeline" />
              <h2 className="mt-6 font-display font-bold text-[#090a0f] text-4xl sm:text-5xl lg:text-[56px] tracking-tight leading-[1.02]">
                Eight steps, end to end.
              </h2>
            </div>
            <div className="lg:col-span-5">
              <p className="text-[#52525b] text-[17px] leading-relaxed">
                Every step is reproducible. Every step is logged to the audit
                ledger. Every step is replaceable by configuration.
              </p>
            </div>
          </div>

          <EditorialBanner
            variant="light"
            gradient="paper-violet"
            image="https://images.unsplash.com/photo-1518770660439-4636190af475?auto=format&fit=crop&w=2400&q=80"
            kicker="Under the hood"
            headline="An engine for two outcomes. Built to be explained."
            chips={[
              { c: "#0047ff", t: "Deterministic" },
              { c: "#7c3aed", t: "Replayable" },
              { c: "#f59e0b", t: "Cited" },
            ]}
            testId="product-pipeline-banner"
          />

          <div className="space-y-px bg-[#e4e4e7] border border-[#e4e4e7]">
            {PIPELINE.map((s, i) => {
              const Icon = s.icon;
              const ACCENTS = [
                { hex: "#0047ff", soft: "rgba(0,71,255,0.10)" },
                { hex: "#00b8c9", soft: "rgba(0,229,255,0.14)" },
                { hex: "#7c3aed", soft: "rgba(124,58,237,0.10)" },
                { hex: "#f59e0b", soft: "rgba(245,158,11,0.12)" },
                { hex: "#10b981", soft: "rgba(16,185,129,0.10)" },
                { hex: "#f43f5e", soft: "rgba(244,63,94,0.10)" },
                { hex: "#0047ff", soft: "rgba(0,71,255,0.10)" },
                { hex: "#7c3aed", soft: "rgba(124,58,237,0.10)" },
              ];
              const a = ACCENTS[i % ACCENTS.length];
              return (
                <div
                  key={s.idx}
                  data-testid={`pipeline-${s.idx}`}
                  className="bg-white grid grid-cols-12 gap-4 items-start p-7 lg:p-9 group hover:bg-[#fafafa] transition-colors relative"
                >
                  <span
                    className="absolute left-0 top-0 bottom-0 w-[3px]"
                    style={{ backgroundColor: a.hex }}
                  />
                  <div className="col-span-2 lg:col-span-1 flex items-center">
                    <span
                      className="font-mono text-[12px] tracking-[0.22em] font-semibold"
                      style={{ color: a.hex }}
                    >
                      {s.idx}
                    </span>
                  </div>
                  <div className="col-span-2 lg:col-span-1 flex items-center">
                    <span
                      className="inline-flex items-center justify-center w-9 h-9"
                      style={{ backgroundColor: a.soft }}
                    >
                      <Icon
                        className="w-5 h-5"
                        style={{ color: a.hex }}
                        strokeWidth={2}
                      />
                    </span>
                  </div>
                  <div className="col-span-8 lg:col-span-3">
                    <p className="font-display font-bold text-[#090a0f] text-[22px] tracking-tight">
                      {s.title}
                    </p>
                  </div>
                  <div className="col-span-12 lg:col-span-7 mt-2 lg:mt-0">
                    <p className="text-[#52525b] text-[15px] leading-relaxed">{s.body}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Missed Risk Deep Dive (entity resolution & full context) */}
      <section id="entity-resolution" className="dark-section py-24 lg:py-32 relative overflow-hidden">
        <div className="absolute inset-0 bg-dot-grid opacity-40" />
        <div className="relative max-w-[1380px] mx-auto px-6 lg:px-10">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 mb-14">
            <div className="lg:col-span-7">
              <SectionLabel index="02" label="Missed risk — entity resolution + full context" dark />
              <h2 className="mt-6 font-display font-bold text-white text-4xl sm:text-5xl lg:text-[56px] tracking-tight leading-[1.02]">
                Fragments &rarr;{" "}
                <span className="text-white/55">risk you can finally see.</span>
              </h2>
            </div>
            <div className="lg:col-span-5">
              <p className="text-white/65 text-[17px] leading-relaxed">
                Without entity resolution and cross-system context, real risks
                stay invisible &mdash; a UBO link, a shared device, a
                cross-jurisdictional fan-out. Sentinel surfaces them, without
                forcing a CIF migration or crossing your data wall.
              </p>
            </div>
          </div>

          <EditorialBanner
            gradient="emerald-cyan"
            image="https://images.unsplash.com/photo-1486325212027-8081e485255e?auto=format&fit=crop&w=2400&q=80"
            kicker="Problem 02 · Missed risk"
            headline="One canonical entity, drawn from many records."
            chips={[
              { c: "#10b981", t: "Per-source isolated" },
              { c: "#00e5ff", t: "No CIF migration" },
              { c: "#7c3aed", t: "Group-level oversight" },
            ]}
            testId="product-er-banner"
          />

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-px bg-white/10 border border-white/10">
            {[
              { t: "Fragments in", b: "Identity records arrive from many sources, isolated." },
              { t: "Resolution", b: "Deterministic signals combined into canonical entities, with confidence." },
              { t: "Group view", b: "Group compliance sees the linkage. Sources stay isolated." },
            ].map((s, i) => (
              <div key={s.t} className="bg-[#0d0e16] p-7 min-h-[180px]">
                <p className="font-mono text-[10px] tracking-[0.22em] uppercase text-[#00e5ff]">
                  Stage / 0{i + 1}
                </p>
                <p className="mt-3 font-display font-bold text-white text-[20px] tracking-tight">
                  {s.t}
                </p>
                <p className="mt-2 text-white/65 text-[13.5px] leading-relaxed">{s.b}</p>
              </div>
            ))}
          </div>

          <div className="mt-10 border border-white/10 bg-[#0d0e16] p-6 lg:p-8 flex flex-col lg:flex-row lg:items-center justify-between gap-4">
            <p className="text-white text-[15px]">
              <span className="font-mono text-[#00e5ff] mr-2">Isolation guarantee:</span>
              Sources see only their own data. Group compliance sees the linkage.
            </p>
            <span className="font-mono text-[11px] tracking-[0.22em] uppercase text-white/45">
              Per-tenant key management
            </span>
          </div>
        </div>
      </section>

      {/* FPRE & FFPRE Deep Dive */}
      <section id="ffpre" className="light-section py-24 lg:py-32">
        <div className="max-w-[1380px] mx-auto px-6 lg:px-10">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 mb-14">
            <div className="lg:col-span-7">
              <SectionLabel index="03" label="FPRE & FFPRE — False-positive reduction" />
              <h2 className="mt-6 font-display font-bold text-[#090a0f] text-4xl sm:text-5xl lg:text-[56px] tracking-tight leading-[1.02]">
                Two engines.<br />
                <span className="text-[#52525b]">One pattern, applied twice.</span>
              </h2>
            </div>
            <div className="lg:col-span-5">
              <p className="text-[#52525b] text-[17px] leading-relaxed">
                Sentinel runs two false-positive reduction engines on the same
                deterministic rails. <strong className="text-[#090a0f]">FPRE</strong> shrinks
                the AML alert backlog. <strong className="text-[#090a0f]">FFPRE</strong> shrinks
                the fraud alert backlog. Neither auto-dismisses — both
                hand the analyst a structured rationale and a calibrated FP
                confidence band.
              </p>
            </div>
          </div>

          <EditorialBanner
            variant="light"
            gradient="paper-violet"
            image="https://images.unsplash.com/photo-1551836022-d5d88e9218df?auto=format&fit=crop&w=2400&q=80"
            kicker="Problem 01 · False-positive reduction"
            headline="From half-hour cases to five-minute decisions."
            chips={[
              { c: "#7c3aed", t: "FPRE · AML" },
              { c: "#f43f5e", t: "FFPRE · Fraud" },
              { c: "#10b981", t: "Officer decides" },
            ]}
            testId="product-fpre-banner"
          />

          {/* Two engine cards */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-px bg-[#e4e4e7] border border-[#e4e4e7] mb-px">
            {[
              {
                tag: "FPRE",
                kind: "AML alerts",
                title: "False Positive Reduction Engine",
                body: "Triages sanctions, PEP, adverse-media and transaction-monitoring alerts. Structured rationale, cited.",
                accent: "text-[#0047ff]",
              },
              {
                tag: "FFPRE",
                kind: "Fraud alerts",
                title: "Fraud False Positive Reduction Engine",
                body: "Triages card and A2A fraud alerts with cross-source context, so the analyst opens with the answer half-written.",
                accent: "text-[#0047ff]",
              },
            ].map((e) => (
              <div
                key={e.tag}
                data-testid={`engine-${e.tag.toLowerCase()}`}
                className="bg-white p-8 lg:p-10 flex flex-col gap-5 min-h-[260px]"
              >
                <div className="flex items-center justify-between">
                  <span className={`font-mono text-[11px] tracking-[0.22em] uppercase ${e.accent}`}>
                    {e.tag}
                  </span>
                  <span className="font-mono text-[10px] tracking-[0.22em] uppercase text-[#a1a1aa]">
                    {e.kind}
                  </span>
                </div>
                <p className="font-display font-bold text-[#090a0f] text-[24px] tracking-tight leading-tight">
                  {e.title}
                </p>
                <p className="text-[#52525b] text-[14px] leading-relaxed">{e.body}</p>
              </div>
            ))}
          </div>

          {/* Shared overview */}
          <p className="mt-12 mb-6 font-mono text-[11px] tracking-[0.22em] uppercase text-[#a1a1aa]">
            Shared pipeline — same engine pattern, applied twice
          </p>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-px bg-[#e4e4e7] border border-[#e4e4e7]">
            {[
              { t: "Trigger", b: "An alert lands from your stack." },
              { t: "Triage", b: "Contextualised against history and cohort." },
              { t: "Officer", b: "Decides — with rationale and citation." },
            ].map((s, i) => (
              <div key={s.t} className="bg-white p-7 min-h-[170px] flex flex-col gap-3">
                <p className="font-mono text-[10px] tracking-[0.22em] uppercase text-[#a1a1aa]">
                  0{i + 1}
                </p>
                <p className="font-display font-bold text-[#090a0f] text-[20px] tracking-tight">
                  {s.t}
                </p>
                <p
                  className="text-[#52525b] text-[13.5px] leading-relaxed"
                  dangerouslySetInnerHTML={{ __html: s.b }}
                />
                <div className="mt-auto flex items-center gap-2 text-[#0047ff]">
                  <CheckCircle2 className="w-4 h-4" />
                  <span className="text-[12px] font-mono uppercase tracking-[0.16em]">Deterministic</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Audit Ledger */}
      <section className="dark-section py-24 lg:py-32 border-t border-white/10">
        <div className="max-w-[1380px] mx-auto px-6 lg:px-10">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-center">
            <div className="lg:col-span-6">
              <SectionLabel index="04" label="Immutable audit ledger" dark />
              <h2 className="mt-6 font-display font-bold text-white text-4xl sm:text-5xl lg:text-[56px] tracking-tight leading-[1.02]">
                Every score, every change,<br />
                hash-chained.
              </h2>
              <p className="mt-6 text-white/65 text-[17px] leading-relaxed max-w-[52ch]">
                Append-only, hash-chained ledger of every assessment, decision
                and configuration change. Tamper-evident. Replayable.
              </p>
              <div className="mt-8 flex flex-wrap gap-2">
                {["Hash-chained", "Append-only", "Replayable", "EU-resident"].map((t) => (
                  <span
                    key={t}
                    className="font-mono text-[10px] tracking-[0.18em] uppercase text-white/70 border border-white/15 px-2.5 py-1.5"
                  >
                    {t}
                  </span>
                ))}
              </div>
            </div>

            <div className="lg:col-span-6">
              <div className="border border-white/10 bg-[#0d0e16] overflow-hidden">
                <div className="px-5 py-3 border-b border-white/10 flex items-center justify-between">
                  <span className="font-mono text-[11px] tracking-[0.18em] uppercase text-white/45">
                    audit_ledger.tail()
                  </span>
                  <span className="font-mono text-[11px] text-[#00e5ff]">live</span>
                </div>
                <div className="font-mono text-[12.5px] text-white/85 leading-relaxed divide-y divide-white/5">
                  {[
                    { t: "08:14:02", h: "0x8f3a…b1c2", a: "ENTITY MERGE", n: "Linked across sources" },
                    { t: "08:14:02", h: "0x91d4…a73f", a: "TRUST SCORE", n: "Critical band" },
                    { t: "08:14:03", h: "0x5e22…77bb", a: "GAP VERDICT", n: "Too soft vs. own rating" },
                    { t: "08:14:08", h: "0x2bf1…0e93", a: "FFPRE", n: "Triaged · rationale stored" },
                    { t: "08:14:11", h: "0xa55c…d409", a: "OFFICER", n: "Decision · escalated" },
                  ].map((r, i) => (
                    <div key={i} className="px-5 py-3 grid grid-cols-12 gap-3 items-center hover:bg-white/[0.03]">
                      <span className="col-span-2 text-white/45">{r.t}</span>
                      <span className="col-span-3 text-[#00e5ff]">{r.h}</span>
                      <span className="col-span-3 text-white">{r.a}</span>
                      <span className="col-span-4 text-white/70 truncate">{r.n}</span>
                    </div>
                  ))}
                </div>
                <div className="px-5 py-3 border-t border-white/10 flex items-center justify-between">
                  <span className="font-mono text-[11px] text-white/45">CHAIN VERIFIED</span>
                  <ArrowRight className="w-3.5 h-3.5 text-[#00e5ff]" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
